import { useEffect, useState, type ReactElement } from 'react';
import type { ApiClient } from './auth/apiClient.js';
import type { DecisionQuery, DecisionRecord } from '@lapo/core';

/**
 * LEDGER-10 (D24) — reverse surfacing. A compact "Decisions" section for the Contact detail + Initiative
 * views: "decisions involving this person / affecting this initiative". Backed by the array-contains facet
 * queries (`participantContactId` / `initiativeId`), each row links into the Ledger detail panel.
 */
export function DecisionsSection({
  api,
  query,
  onOpen,
  title = 'Decisions',
}: {
  api: Pick<ApiClient, 'getDecisions'>;
  query: DecisionQuery;
  onOpen: (decisionId: string) => void;
  title?: string;
}): ReactElement | null {
  const [rows, setRows] = useState<DecisionRecord[] | null>(null);

  useEffect(() => {
    let live = true;
    void api.getDecisions(query).then((d) => {
      if (live) setRows(d);
    });
    return () => {
      live = false;
    };
  }, [api, query.participantContactId, query.initiativeId]);

  if (!rows || rows.length === 0) return null;
  return (
    <section className="lapo-decisions-section" data-testid="reverse-decisions">
      <h3 className="lapo-decisions-section-head">{title}</h3>
      <ul className="lapo-decisions-section-list">
        {rows.map((d) => (
          <li key={d.decisionId}>
            <button type="button" className="lapo-decisions-section-row" onClick={() => onOpen(d.decisionId)}>
              <span className="lapo-decisions-section-summary">{d.summary}</span>
              <span className="lapo-decisions-section-date">{d.dateKey}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
